import { makeAutoObservable, reaction, runInAction } from 'mobx';
import type RootStore from './RootStore';

type ConnectionListener = (isOnline: boolean) => void | Promise<void>;

class ConnectionStore {
  debugMode = false;
  serverConnectionStatus = false;
  isOffline = !navigator.onLine;
  networkType = 'unknown';
  networkSpeed = 'unknown';

  rootStore: RootStore;

  serverConnectionStatusDispose: (() => void) | null = null;
  private connectionListeners: Set<ConnectionListener> = new Set();

  /**
   * @param {object} rootStore
   */
  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    this.rootStore = rootStore;
    
    // Setup browser online/offline listeners
    this.setupNetworkListeners();
    
    if (rootStore?.app) {
      rootStore.app.io.on('disconnect', () => {
        this.handleDisconnect();
      });

      rootStore.app.io.on('connect', () => {
        this.handleConnect('Connect');
      });

      rootStore.app.io.on('reconnect', () => {
        this.handleConnect('Reconnect');
      });
    }
    if (rootStore?.notificationStore) {
      this.serverConnectionStatusDispose = reaction(
        () => this.serverConnectionStatus,
        (connectionStatus) =>
          rootStore.notificationStore?.addNotification({
            message: connectionStatus
              ? 'Server Connected'
              : 'Server Disconnected',
            options: {
              variant: connectionStatus ? 'success' : 'error',
              autoHideduration: 3000,
            },
          })
      );
    }
  }

  private setupNetworkListeners() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Check network information if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        this.updateNetworkInfo(connection);
        connection.addEventListener('change', () => {
          this.updateNetworkInfo(connection);
        });
      }
    }
  }

  private handleOnline = () => {
    runInAction(() => {
      this.isOffline = false;
    });
    
    // Notify all listeners
    this.connectionListeners.forEach(listener => {
      listener(true);
    });
    
    // Trigger offline queue processing
    if (this.rootStore.offlineQueueManager) {
      // Process queue after a short delay to ensure connection is stable
      setTimeout(() => {
        this.rootStore.offlineQueueManager?.processQueue(async () => false);
      }, 1000);
    }
    
    if (this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: 'Back online',
        options: {
          variant: 'info',
          autoHideduration: 2000,
        },
      });
    }
  };

  private handleOffline = () => {
    runInAction(() => {
      this.isOffline = true;
    });
    
    // Notify all listeners
    this.connectionListeners.forEach(listener => {
      listener(false);
    });
    
    if (this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: 'Working offline - changes will sync when reconnected',
        options: {
          variant: 'warning',
          autoHideduration: 5000,
        },
      });
    }
  };

  private updateNetworkInfo(connection: any) {
    runInAction(() => {
      this.networkType = connection.effectiveType || 'unknown';
      this.networkSpeed = connection.downlink ? `${connection.downlink} Mbps` : 'unknown';
    });
  }

  addConnectionListener(listener: ConnectionListener) {
    this.connectionListeners.add(listener);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  removeConnectionListener(listener: ConnectionListener) {
    this.connectionListeners.delete(listener);
  }

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  dispose = () => {
    this.serverConnectionStatusDispose && this.serverConnectionStatusDispose();
    
    // Clean up network listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    
    // Clear connection listeners
    this.connectionListeners.clear();
  };

  setServerConnectionStatus = (/** @type {boolean} */ value: boolean) => {
    this.debugMode && console.warn('Debug setServerConnectionStatus', value);
    this.serverConnectionStatus = value;
  };

  handleDisconnect = async () => {
    this.debugMode && console.warn('Debug handleDisconnect called');
    this.setServerConnectionStatus(false);
    if (this.rootStore.channelStore) {
      const { channelStore } = this.rootStore;
      channelStore.resetChannelItems();
    }
    if (!this.rootStore || !this.rootStore.notificationStore)
      console.log('Server Disconnected');
    if (this.rootStore.routerStore) {
      const { routerStore } = this.rootStore;
      routerStore.clearRouteChannels();
    }
  };

  handleConnect = async (/** @type {string} */ calledFrom: string) => {
    this.debugMode && console.warn('Debug handleConnect called');
    this.setServerConnectionStatus(true);
    if (!this.rootStore || !this.rootStore.notificationStore)
      console.log('Server', calledFrom);
    const { channelStore, app } = this.rootStore;
    if (
      channelStore &&
      app &&
      app.io &&
      app.io.id &&
      channelStore.websocketConnection !== app.io.id
    ) {
      this.debugMode &&
        console.warn('Debug handleConnect setWebsocketConnection', app.io.id);
      channelStore.setWebsocketConnection(app.io.id);
    }
    if (this.rootStore.routerStore) {
      const { routerStore, authStore } = this.rootStore;
      if (routerStore.currentRoute && authStore?.isSignedIn)
        routerStore.triggerEnterSideEffects();
      if (calledFrom === 'Reconnect' || !routerStore.lastSetChannels)
        return routerStore.setRouteChannels();
    }
  };

  get connectionMessage() {
    return this.serverConnectionStatus ? 'Connected' : 'Not Connected';
  }

  get statusColor() {
    const authStatus = this.rootStore.authStore?.isSignedIn;
    if (this.rootStore.viewStore?.appUpdateFound && authStatus) return 'info';
    if (this.serverConnectionStatus && authStatus) return 'success';
    if (this.serverConnectionStatus && !authStatus) return 'warning';
    if (!this.serverConnectionStatus && !authStatus) return 'danger';
    return 'gray';
  }
}
export default ConnectionStore;
